import './index.css'

import React, { useEffect, useRef } from 'react'
import Modal from 'react-modal'
import { Trans } from '@disco/i18n/trans'
import { useTranslation } from '@disco/i18n/use-translation'
import { downloadInFrameIfSupported } from '@disco/utils/target-iframe'
import modalStyles from 'components/utils/modalStyles'
type Props = {
  type: 'base' | 'loading' | 'error' | 'deferred'
  onClose: (...args: Array<any>) => any
  onClick?: (...args: Array<any>) => any
  url?: string | null | undefined
}

// `common` rather than a page namespace: this modal renders from the `app`
// entry (the contacts and mailing list exports) and from `play` (the download
// selection on public share pages).
const DownloadModal = (props: Props): React.ReactNode => {
  const { t } = useTranslation('common')
  const ref = useRef<HTMLAnchorElement>(null)
  useEffect(() => {
    if (props.url && ref.current) {
      ref.current.click()
    }
  }, [props.url])

  const linkProps = {
    ref,
    className: 'DownloadModal_Link DownloadModal_Text is-link',
    download: true,
    href: props.url ?? undefined,
    target: downloadInFrameIfSupported() ?? undefined,
    onClick: props.onClick,
  }

  const renderDownload = () => {
    switch (props.type) {
      case 'base':
        return (
          <div className="DownloadModal_Link">
            <i className="Icon download" />
            {t(
              'common:downloadShouldStartShortly',
              'Your download should start very shortly.',
            )}
            <br />
            <Trans
              t={t}
              i18nKey="common:ifYourFileDoesntStartDownloading"
              defaults={
                'If your file doesn’t start downloading within a minute or two, <download>click here</download>.'
              }
              components={{
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                download: <a {...linkProps} />,
              }}
            />
          </div>
        )

      case 'error':
        return (
          <button className="DownloadModal_Link" onClick={props.onClick}>
            <i className="Icon error" />
            <div className="Download_Text is-error">
              {t('common:errorClickToRetry', 'Error, click to retry')}
            </div>
          </button>
        )

      case 'loading':
        return (
          <div className="DownloadModal_Link">
            <i className="Icon loading" />
            <div className="Download_Text ">
              {t('common:generatingDownload', 'Generating Download')}
            </div>
          </div>
        )

      case 'deferred':
        return (
          <div className="DownloadModal_Link">
            <div className="Download_Text ">
              {t(
                'common:generatingYourExportNow',
                'We’re generating your export now, and will email it to you as soon as it’s ready. Please note, during peak periods, reports can take up to an hour to generate.',
              )}
            </div>
          </div>
        )

      default:
        break
    }
  }

  return (
    <Modal onRequestClose={props.onClose} isOpen style={modalStyles}>
      <div className="DownloadModal" onClick={e => e.stopPropagation()}>
        {renderDownload()}
      </div>
    </Modal>
  )
}

export default DownloadModal
